import { v4 as uuidv4 } from "uuid";
import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import EmotionBubblePreview from "../../features/previews/emotion_bubble/EmotionBubblePreview";
import EmotionBubbleNodePreview from "../../features/sequence/node_previews/EmotionBubbleNodePreview";
import { ValidationHelper } from "../../features/validation/ValidationHelper";
import { Actor } from "../Actor";
import { InteractionProperty, InteractionPropertyType } from "../InteractionProperty";
import { Prop } from "../Prop";
import { IAnswer } from "../Quiz";
import Take from "../Take";
import Take3D from "../Take3D";
import Interaction, { InteractionType } from "./Interaction";
import {
    defaultMessageBubbleAnimation,
    getLookAtTargetMissingWarning,
    getMessageBubbleAnimationSearchValue,
    MessageBubbleAnimation,
    validateBubbleAnchorInTake,
    validateLookAtNotSelf,
} from "./MessageBubble";
import {
    compactSearchableStrings,
    SearchLookupContext,
} from "./SearchableInteractionValues";

export const emotionBubbleStyles: string[] = [
    "Happy",
    "Sad",
    "Thumb",
    "ThumbsDown",
    "Annoyed",
    "CallEmoji",
    "Disapprove",
    "Doubt",
    "Exclame",
    "Gibberish",
    "Headset",
    "Panic",
    "Phone",
    "Sick",
    "VeryHappy",
    "WalkieTalkie",
];

type EmotionBubbleStyle = {
    name: string;
    path: string;
    displayName: string;
};

export const emotionBubbleStylesMapped: EmotionBubbleStyle[] = [
    {
        name: "Happy",
        path: "/emotions/Emoticon_Happy.png",
        displayName: "Happy",
    },
    {
        name: "Sad",
        path: "/emotions/Emoticon_Sad.png",
        displayName: "Sad",
    },
    {
        name: "Thumb",
        path: "/emotions/Emoticon_HappyThumb.png",
        displayName: "Thumbs up",
    },
    {
        name: "ThumbsDown",
        path: "/emotions/Emoticon_DisapproveThumbs.png",
        displayName: "Thumbs down",
    },
    {
        name: "Annoyed",
        path: "/emotions/Emoticon_Annoyed.png",
        displayName: "Annoyed",
    },
    {
        name: "CallEmoji",
        path: "/emotions/Emoticon_Calling.png",
        displayName: "Call Emoji",
    },
    {
        name: "Disapprove",
        path: "/emotions/Emoticon_DisapproveThumb.png",
        displayName: "Disapprove",
    },
    {
        name: "Doubt",
        path: "/emotions/Emoticon_Doubt.png",
        displayName: "Doubt",
    },
    {
        name: "Exclame",
        path: "/emotions/exclame.png",
        displayName: "Exclame",
    },
    {
        name: "Gibberish",
        path: "/emotions/Emoticon_Gibberish.png",
        displayName: "Gibberish",
    },
    {
        name: "Headset",
        path: "/emotions/Emoticon_Headset.png",
        displayName: "Headset",
    },
    {
        name: "Panic",
        path: "/emotions/Emoticon_PanicScared.png",
        displayName: "Panic",
    },
    {
        name: "Phone",
        path: "/emotions/Emoticon_Phone.png",
        displayName: "Phone",
    },
    {
        name: "Sick",
        path: "/emotions/Emoticon_Sick.png",
        displayName: "Sick",
    },
    {
        name: "VeryHappy",
        path: "/emotions/Emoticon_VeryHappy.png",
        displayName: "Very Happy",
    },
    {
        name: "WalkieTalkie",
        path: "/emotions/Emoticon_WalkieTalkie.png",
        displayName: "Walkie Talkie",
    },
];

export interface IEmotionBubble extends Interaction {
    npcId: string;
    messageBubbleStyle: string;
    lookAt: string;
    /** Look at `lookAt` with the head only (FinalIK) instead of the whole body. Missing = whole body. */
    finalIK?: boolean;
    animation: MessageBubbleAnimation | string;
}

const requiredEmotionBubbleProperties: InteractionProperty[] = [
    {
        id: "displayTime",
        name: "Display Time",
        type: InteractionPropertyType.number,
        value: 2,
    },
];

export function CreateEmotionBubble(): IEmotionBubble {
    return {
        id: uuidv4(),
        name: "Emotion Bubble",
        animation: { ...defaultMessageBubbleAnimation },
        lookAt: "",
        finalIK: false,
        messageBubbleStyle: emotionBubbleStylesMapped[0].name,
        npcId: "player",
        type: InteractionType.EmotionBubble,
        properties: requiredEmotionBubbleProperties,
    };
}

export function CreateEmotionBubbleCopy(interaction: IEmotionBubble): IEmotionBubble {
    return {
        id: uuidv4(),
        name: "Emotion Bubble",
        animation: interaction.animation,
        lookAt: interaction.lookAt,
        finalIK: interaction.finalIK ?? false,
        messageBubbleStyle: interaction.messageBubbleStyle,
        npcId: interaction.npcId,
        type: InteractionType.EmotionBubble,
        properties: interaction.properties,
    };
}

export const emotionBubbleInteraction = (): InteractionFactoryEntity => {
    const component = <EmotionBubblePreview />;

    const create = (getKeys: (amount: number) => string[]): IEmotionBubble => {
        return {
            id: uuidv4(),
            name: "Emotion Bubble",
            animation: { ...defaultMessageBubbleAnimation },
            lookAt: "",
            finalIK: false,
            messageBubbleStyle: emotionBubbleStylesMapped[0].name,
            npcId: "player",
            type: InteractionType.EmotionBubble,
            properties: requiredEmotionBubbleProperties,
        };
    };

    const migrate = (from: number, to: number, interaction: Interaction): IEmotionBubble => {
        return interaction as IEmotionBubble;
    };

    const copy = (interaction: Interaction,
        getKeys: (amount: number) => string[],
        replaceKey: (from: string, to: string) => void, getAnswerArray: (originalAnswers: IAnswer[], newKeys: string[]) => IAnswer[]): IEmotionBubble => {

        const messageBubble = interaction as IEmotionBubble;

        return {
            ...messageBubble,
            id: uuidv4(),
        }
    }

    const validate = (interaction: Interaction, getValue: (key: string) => string, take: Take, actorPresets: Actor[], allPropPresets: Prop[] = []): string[] => {
        const take3d = take as Take3D;
        const converted = interaction as IEmotionBubble;
        const warnings: string[] = [];

        validateBubbleAnchorInTake(converted.npcId, take3d, actorPresets, allPropPresets, warnings, converted.id);
        ValidationHelper.ValidateNpcInTake(converted.lookAt, take3d, actorPresets, warnings, getLookAtTargetMissingWarning);
        validateLookAtNotSelf(converted.npcId, converted.lookAt, actorPresets, warnings);

        return warnings;
    }

    const validateErrors = (interaction: Interaction): string[] => {
        const converted = interaction as IEmotionBubble;
        const errors: string[] = [];

        if (!converted.npcId?.trim()) errors.push("Actor or Prop is required");
        if (!getMessageBubbleAnimationSearchValue(converted.animation)?.trim()) errors.push("Animation is required");
        if (!converted.messageBubbleStyle?.trim()) errors.push("Emotion style is required");

        return errors;
    }

    const filter = (
        interaction: Interaction,
        takeName: string,
        getMatchingResults: (
            searchableStrings: string[],
            takeName: string,
            interaction: Interaction,
            filterType: string
        ) => SearchResultData[],
        _getValue: (key: string) => string,
        searchLookupContext?: SearchLookupContext,
    ): SearchResultData[] => {

        const converted = interaction as IEmotionBubble;
        const searchableStrings = compactSearchableStrings(
            converted.name,
            searchLookupContext?.getObjectSearchTerms(converted.npcId) ?? converted.npcId,
            searchLookupContext?.getObjectSearchTerms(converted.lookAt) ?? converted.lookAt,
            getMessageBubbleAnimationSearchValue(converted.animation),
            converted.messageBubbleStyle,
        );

        const resultsMessages = getMatchingResults(
            searchableStrings,
            takeName,
            interaction,
            filterType,
        );

        const actorSearchTerms = searchLookupContext?.getActorSearchTerms(converted.npcId) ?? [];
        const isActorAnchor = !searchLookupContext || actorSearchTerms.length > 1;
        if (isActorAnchor) {
            resultsMessages.forEach((message) => { message.profilePicture = converted.npcId });
        }

        return resultsMessages;
    };

    const filterType: string = "Emotion Bubble";

    return {
        View: component,
        NodeView: (nodeId: string, interaction: Interaction) => (
            <EmotionBubbleNodePreview nodeId={nodeId} interaction={interaction} />
        ),
        FilterType: filterType,
        Create: create,
        Migrate: migrate,
        Copy: copy,
        Validate: validate,
        ValidateErrors: validateErrors,
        Filter: filter,
    };
};


